import { Component, OnDestroy, OnInit } from '@angular/core';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { SLIDER_CONFIG } from '../../../../shared/constants/constants';
import {
  FaqModel,
  QuestionModel,
  TopicModel,
} from '../../../../shared/models/faq.model';
import { FaqService } from '../../../../shared/services/faq.service';
import { SharedModule } from '../../../../shared/shared.module';
import { AddEditQuestionComponent } from '../add-edit-question/add-edit-question.component';
import { AddEditTopicComponent } from '../add-edit-topic/add-edit-topic.component';
import { TopicCarouselComponent } from '../topic-carousel/topic-carousel.component';
import { TopicQuestionComponent } from '../topic-question/topic-question.component';

@Component({
  selector: 'app-faq',
  standalone: true,
  templateUrl: './faq.component.html',
  styleUrl: './faq.component.scss',
  imports: [
    ButtonComponent,
    AddEditTopicComponent,
    SharedModule,
    AddEditQuestionComponent,
    TopicQuestionComponent,
    ModalComponent,
    SlickCarouselModule,
    SkeletonLoaderComponent,
    TopicCarouselComponent,
    PageHeaderComponent,
  ],
})
export class FaqComponent implements OnInit, OnDestroy {
  showTopicModal = false;
  showQuestionModal = false;
  editTopic: TopicModel | null = null;
  editQuestion: QuestionModel | null = null;
  faqList: FaqModel[] = [];
  deleteLoading = false;
  selectedTopic: FaqModel | null = null;
  slides: any;
  slideConfig = SLIDER_CONFIG;
  subscription = new Subscription();
  faqLoading = true;
  menuOpenIndex = -1;
  search = '';
  questionLoading = false;
  constructor(private faqService: FaqService, private toastr: ToastrService) {}

  ngOnInit(): void {
    this.getFaqList();
  }

  getFaqList() {
    this.faqLoading = true;
    this.faqService.getFaqList().subscribe({
      next: (res) => {
        this.selectedTopic =
          this.faqList.length === 0
            ? res.result?.[0]
            : res.result.filter(
                (el: any) => el?.id === this.selectedTopic?.id
              )?.[0] || res.result?.[0];
        this.faqList = [...res.result];
        this.faqLoading = false;
      },
      error: (res) => {
        this.faqLoading = false;
        console.error(res);
      },
    });
  }

  toggleTopicModal(state: boolean) {
    this.showTopicModal = state;
    if (!state) {
      this.editTopic = null;
    }
  }

  toggleQuestionModal(state: boolean) {
    this.showQuestionModal = state;
    if (!state) {
      this.editQuestion = null;
    }
  }

  onEditTopic(faq: FaqModel) {
    const { imgKey, id, name, img } = faq;
    this.editTopic = { img, id, name, imgKey };
    this.toggleTopicModal(true);
  }

  onFaqUpdate() {
    this.toggleTopicModal(false);
    this.toggleQuestionModal(false);
    this.getFaqList();
  }

  onSubmit(data: QuestionModel) {
    const { answer, question } = data;
    this.questionLoading = true;
    if (this.editQuestion && this.editQuestion?.id) {
      this.faqService
        .updateQuestion(this.editQuestion.id, {
          question,
          answer,
          topicId: this.selectedTopic?.id || '',
        })
        .subscribe({
          next: (res) => {
            this.toastr.success('Question updated successfully!');
            this.questionLoading = false;
            this.onFaqUpdate();
          },
          error: (err) => {
            this.toastr.error(
              err?.error?.message || err?.message || 'Something went wrong!'
            );
            this.questionLoading = false;
          },
        });
    } else {
      this.faqService
        .addQuestion({
          question,
          answer,
          topicId: this.selectedTopic?.id || '',
        })
        .subscribe({
          next: (res) => {
            this.toastr.success('Question added successfully!');
            this.questionLoading = false;
            this.onFaqUpdate();
          },
          error: (err) => {
            this.toastr.error(
              err?.error?.message || err?.message || 'Something went wrong!'
            );
            this.questionLoading = false;
          },
        });
    }
  }
  onEditQuestion(index: number) {
    let filteredQuestions = this.selectedTopic?.faqs.filter((question) =>
      question.question.toLowerCase().includes(this.search.toLowerCase())
    );
    this.editQuestion = filteredQuestions?.[index] || null;
    this.toggleQuestionModal(true);
  }

  onFaqClick(faq: FaqModel) {
    this.selectedTopic = faq;
  }

  handleSearch(searchText: string) {
    this.search = searchText;
    this.searchTopic(searchText?.trim());
  }

  searchTopic(value: string) {
    const filteredList = this.faqList.filter(
      (faq) =>
        faq.faqs.findIndex((question) =>
          question.question.toLowerCase().includes(value.toLowerCase())
        ) > -1
    );
    if (filteredList.length > 0) {
      this.selectedTopic =
        this.faqList.find((faq) => faq.id === filteredList[0].id) ||
        this.faqList?.[0] ||
        null;
    }
  }

  handleMenuOpen(index: number) {
    this.menuOpenIndex = index;
  }

  closeAllMenu(index: number) {
    if (index === this.menuOpenIndex) {
      this.menuOpenIndex = -1;
    }
  }

  deleteQuestion(index: number) {
    this.deleteLoading = true;
    if (this.selectedTopic?.faqs?.[index]?.id) {
      this.faqService
        .deleteQuestion(this.selectedTopic?.faqs?.[index]?.id)
        .subscribe({
          next: (res) => {
            this.deleteLoading = false;
            this.toastr.success('Question deleted successfully.');
            this.onFaqUpdate();
          },
          error: (err) => {
            console.error(err);
            this.toastr.error(
              err?.error?.message || err?.message || 'Something went wrong!'
            );
          },
        });
    }
  }
  get faqCount() {
    return this.faqList.reduce((total, el) => total + el.faqs.length, 0);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
